import styled from '@emotion/styled';
import { uiTheme as t } from './theme';
import FieldError from './FieldError';

export interface TextFieldProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Tipo del campo. `'password'` para secretos; por omisión, texto. */
  type?: 'text' | 'password';
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  maxLength?: number;
  'aria-describedby'?: string;
}

/**
 * Estilo compartido por todos los controles de texto de esta carpeta.
 *
 * No define un ancho fijo: ocupa el ancho del contenedor que lo envuelve.
 */
export const controlStyles = (invalid: boolean) => ({
  width: '100%',
  boxSizing: 'border-box' as const,
  minHeight: '2.875rem',
  padding: '0.5rem 0.875rem',
  fontFamily: t.font,
  fontSize: '0.875rem',
  color: t.color.titleStrong,
  background: t.color.inputBg,
  border: `1px solid ${invalid ? t.color.danger : t.color.inputBorder}`,
  borderRadius: t.radius.sm,
  outline: 'none',
  transition: 'border-color 0.15s ease',
  '&:focus': { borderColor: t.color.primary },
  '&:disabled': {
    background: t.color.disabledSurface,
    color: t.color.disabled,
    cursor: 'not-allowed',
  },
});

const Input = styled.input<{ invalid: boolean }>(({ invalid }) => controlStyles(invalid));

/**
 * Campo de texto de una sola línea.
 */
const TextField = ({
  id,
  value,
  onChange,
  placeholder,
  error,
  disabled,
  maxLength,
  type = 'text',
  ...rest
}: TextFieldProps) => {
  const describedBy =
    [rest['aria-describedby'], error ? `${id}-error` : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <>
      <Input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        invalid={!!error}
        aria-invalid={!!error}
        aria-describedby={describedBy}
        onChange={event => onChange(event.target.value)}
      />
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </>
  );
};

export default TextField;
